/**
 * "Is it working?": the project Home's top strip (design "A calmer project Home", boards 1-3). The name and its
 * one-line description (editable in place), who is here (avatars only: what each does is in the docked "In this
 * Project" panel), one status chip, then the actions (Open app · Start/Stop all · Continue in chat) and a "⋯" menu
 * with Rename, Edit description, Add to / Remove from favorites (projects/useProjectFavorite), Remix (a copy of the
 * project as a new one of yours), Archive (or Unarchive, for its owner: projects/ArchivedProjects) and Delete project. Under the
 * description, the workspaces the project is in (chips; its owner adds it to more or takes it out there).
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Dropdown } from 'antd';
import { Archive, Copy, DotsThree, Star } from '@phosphor-icons/react';
import Icon from '../icons/Icon';
import { ParticipantMark, listedAgents } from '../presence/Presence';
import { agentName, othersOf } from '../../utils/presenceModel';

const MAX_MARKS = 4;

/** You, then the others and their AI agents, at most four marks, and "4 here" / "Only you". */
export function HereAvatars({ snapshot }) {
  const you = snapshot?.you;
  if (!you) { return null; }
  const others = othersOf(snapshot);
  const agents = listedAgents(snapshot);
  const marks = [
    { key: `p:${you.id}`, person: you, label: `${you.name || 'You'} (you)` },
    ...others.map((person) => ({ key: `p:${person.id}`, person, label: person.name || 'Someone' })),
    ...agents.map((agent) => ({ key: `a:${agent.id}`, agent, label: agentName(agent, you) })),
  ];
  const count = marks.length;
  const label = count === 1 ? 'Only you' : `${count} here`;
  return (
    <span aria-label={`${label}: ${marks.map((mark) => mark.label).join(', ')}`} className="ph-here" data-testid="home-here" role="group">
      <span className="ph-here-marks">
        {marks.slice(0, MAX_MARKS).map((mark) => (mark.person ? (
          <ParticipantMark idle={mark.person.state === 'idle'} initials={mark.person.initials || '?'} key={mark.key} label={mark.label} size={28} slot={mark.person.slot ?? 0} />
        ) : (
          <ParticipantMark agent initials={mark.agent.driver?.initials || 'AI'} key={mark.key} label={mark.label} paused={Boolean(mark.agent.paused)} size={28} slot={mark.agent.slot ?? 0} />
        )))}
      </span>
      <span className="ph-muted">{label}</span>
    </span>
  );
}

HereAvatars.propTypes = { snapshot: PropTypes.object };

export default function HomeStrip({
  name, description, canEdit = true, canDelete = true, onSave, onDelete, deleting = false, presence = null, status = null, children = null,
  onRemix = null, lean = false, workspaces = null, archived = false, onArchive = null, onUnarchive = null,
  favorite = false, onToggleFavorite = null,
}) {
  const [editing, setEditing] = useState(null); // 'name' | 'description' | null
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const start = (field) => {
    if (!canEdit) { return; }
    setError(null);
    setEditing(field);
    setValue((field === 'name' ? name : description) || '');
  };
  const cancel = () => { setEditing(null); setValue(''); setError(null); };
  const confirm = async() => {
    const field = editing;
    if (!field) { return; }
    const next = value.trim();
    if ((field === 'name' && !next) || next === ((field === 'name' ? name : description) || '')) { cancel(); return; }
    setSaving(true);
    const failure = await onSave(field, next);
    setSaving(false);
    if (failure) { setError(failure); return; }
    cancel();
  };
  const onKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); confirm(); }
    if (event.key === 'Escape') { cancel(); }
  };

  const menu = [
    ...(canEdit ? [
      { key: 'rename', label: 'Rename', icon: <Icon name="edit" size={14} /> },
      { key: 'description', label: description ? 'Edit description' : 'Add a description', icon: <Icon name="edit" size={14} /> },
    ] : []),
    ...(onToggleFavorite ? [{
      key: 'favorite',
      label: favorite ? 'Remove from favorites' : 'Add to favorites',
      icon: <Star aria-hidden size={14} weight={favorite ? 'fill' : 'regular'} />,
    }] : []),
    ...(onRemix ? [{ key: 'remix', label: 'Remix…', icon: <Copy aria-hidden size={14} /> }] : []),
    ...(!archived && onArchive ? [{ key: 'archive', label: 'Archive project…', icon: <Archive aria-hidden size={14} /> }] : []),
    ...(archived && onUnarchive ? [{ key: 'unarchive', label: 'Unarchive', icon: <Archive aria-hidden size={14} /> }] : []),
    ...((canEdit || onToggleFavorite || onRemix || onArchive || onUnarchive) && canDelete ? [{ type: 'divider' }] : []),
    ...(canDelete ? [{ key: 'delete', label: deleting ? 'Deleting…' : 'Delete project…', danger: true, disabled: deleting, icon: <Icon name="trash" size={14} /> }] : []),
  ];
  const onMenu = ({ key }) => {
    if (key === 'rename') { start('name'); }
    if (key === 'description') { start('description'); }
    if (key === 'delete') { onDelete?.(); }
    if (key === 'favorite') { onToggleFavorite?.(); }
    if (key === 'remix') { onRemix?.(); }
    if (key === 'archive') { onArchive?.(); }
    if (key === 'unarchive') { onUnarchive?.(); }
  };

  return (
    <section aria-labelledby="ph-name" className={`ph-card ph-strip${lean ? ' is-lean' : ''}`} data-testid="home-strip">
      <div className="ph-strip-main">
        <div className="ph-strip-title">
          {editing === 'name' ? (
            <input
              aria-label="Project name"
              autoFocus
              className="ph-name-input"
              disabled={saving}
              onBlur={confirm}
              onChange={(event) => setValue(event.target.value)}
              onKeyDown={onKeyDown}
              type="text"
              value={value}
            />
          ) : (
            <h1 id="ph-name">
              {canEdit ? (
                <button className="ph-editable" onClick={() => start('name')} title="Rename" type="button">{name || 'Project'}</button>
              ) : (name || 'Project')}
            </h1>
          )}
          {status}
          <HereAvatars snapshot={presence} />
        </div>
        {editing === 'description' ? (
          <textarea
            aria-label="One-line description"
            autoFocus
            className="ph-description-input"
            disabled={saving}
            onBlur={confirm}
            onChange={(event) => setValue(event.target.value)}
            onKeyDown={onKeyDown}
            rows={2}
            value={value}
          />
        ) : description ? (
          <p className="ph-description">
            {canEdit ? <button className="ph-editable" onClick={() => start('description')} title="Edit the description" type="button">{description}</button> : description}
          </p>
        ) : canEdit ? (
          <button className="ph-link ph-add-description" onClick={() => start('description')} type="button">Add a one-line description</button>
        ) : null}
        {workspaces}
        {error && <p className="ph-error" role="alert">{error}</p>}
      </div>
      {(children || menu.length > 0) && (
        <div className="ph-strip-actions">
          {children}
          {menu.length > 0 && (
            <Dropdown menu={{ items: menu, onClick: onMenu }} placement="bottomRight" trigger={['click']}>
              <button aria-label="More actions: rename, description, delete" className="ph-btn ph-btn-icon" data-testid="home-more-actions" type="button">
                <DotsThree aria-hidden size={18} weight="bold" />
              </button>
            </Dropdown>
          )}
        </div>
      )}
    </section>
  );
}

HomeStrip.propTypes = {
  // An archived project: Unarchive (`onUnarchive`) in the menu instead of Archive project… (`onArchive`).
  archived: PropTypes.bool,
  canDelete: PropTypes.bool,
  canEdit: PropTypes.bool,
  children: PropTypes.node,
  deleting: PropTypes.bool,
  description: PropTypes.string,
  // One of the person's favorites: Remove from favorites (`onToggleFavorite`) in the menu instead of Add to favorites.
  favorite: PropTypes.bool,
  // The gadget Home: no card around the title and its actions.
  lean: PropTypes.bool,
  name: PropTypes.string,
  onArchive: PropTypes.func,
  onDelete: PropTypes.func,
  // "Remix…" in the menu: opens New project in remix mode on this project.
  onRemix: PropTypes.func,
  onSave: PropTypes.func.isRequired,
  onToggleFavorite: PropTypes.func,
  onUnarchive: PropTypes.func,
  presence: PropTypes.object,
  status: PropTypes.node,
  // The project's workspaces (ProjectWorkspaceChips), under the description.
  workspaces: PropTypes.node,
};
